import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Modal, ModalProps } from 'antd';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import WorkOrderSteps from './WorkOrderSteps';
import FragaPartsProvider from './FragaPartsProvider';
import StatusTag from '@/components/_common/StatusTag';
import { isAppPortocar } from '@/index';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';

type Props = Omit<ModalProps, 'onCancel'> & {
  onFinish: VoidFunction;
  onCancel: VoidFunction;
  kit?: { name: string; id: number };
};

export type WorkorderItem = {
  complementId: number;
  itemType: WorkOrderItemType;
  itemId: number;
  itemName: string;
  subAreaId?: number;
  manufacturerId?: number;
  manufacturingCode?: string;
  supplierId?: number;
  isInternal?: boolean;
  quantity?: number;
  unitPrice: number;
  unitCost?: number;
  discount?: number;
  isPaintedPart?: boolean;
  paint?: number;
  change?: number;
  repair?: number;
  configDiscount?: number;
  time?: number;
  kitId?: number;
  kitName?: string;
  observation?: string;
};

type WorkorderItemsContextProps = {
  items: WorkorderItem[];
  setItems: (items: WorkorderItem[]) => void;
  itemType?: WorkOrderItemType;
  setItemType: (itemType: WorkOrderItemType) => void;
  hasNewItem?: boolean;
  setHasNewItem: (value: boolean) => void;
  isLastSetup?: boolean;
  setIsLastSetup: (value: boolean) => void;
  currentStep: number;
  onNextStep: VoidFunction;
  onPrevStep: (value?: number) => void;
  kit?: { name: string; id: number };
};

const WorkorderItemsContext = createContext({} as WorkorderItemsContextProps);

export const useWorkorderItems = () => useContext(WorkorderItemsContext);

const WorkOrderItemModal: React.FC<Props> = ({ open, onCancel, kit }) => {
  const [itemType, setItemType] = useState<WorkOrderItemType>();
  const [items, setItems] = useState<WorkorderItem[]>([]);
  const [hasNewItem, setHasNewItem] = useState<boolean>();
  const [isLastSetup, setIsLastSetup] = useState<boolean>();
  const [currentStep, setCurrentStep] = useState(0);

  function onNextStep() {
    setCurrentStep(currentStep + 1);
  }

  function onPrevStep(value?: number) {
    setCurrentStep(value || currentStep - 1);
  }

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  useEffect(() => {
    setItems([]);
  }, [itemType]);

  useEffect(() => {
    if (currentStep > 1 && !items.length) {
      addNewItemPart();
    }
  }, [currentStep, items]);

  function resetData() {
    setItemType(undefined);
    setItems([]);
  }

  function handleCancel() {
    if (onCancel) {
      setCurrentStep(0);
      resetData();
      onCancel();
    }
  }

  const getMinDiscountPolicy = useCallback(
    (type: WorkOrderItemType) => {
      if (
        isAppPortocar &&
        type === WorkOrderItemType.Service &&
        !workorder?.partnerDiscount &&
        workorder?.employeeDiscount
      )
        return 10;

      if (
        !isAppPortocar ||
        !complement?.policyNumber ||
        type !== WorkOrderItemType.Service ||
        !workorder?.partnerDiscount
      )
        return undefined;

      const isPortoPolicy =
        !!complement?.policyNumber &&
        complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

      return isPortoPolicy ? 20 : 15;
    },
    [
      isAppPortocar,
      complement?.policyNumber,
      complement?.companyCode,
      workorder?.partnerDiscount,
    ],
  );

  async function addNewItemPart() {
    setHasNewItem(false);
    if (!itemType) return;

    setItems([
      {
        complementId: 0,
        itemId: 0,
        itemName: '',
        itemType: itemType,
        unitPrice: 0,
        isInternal: true,
        discount: getMinDiscountPolicy(itemType),
        quantity: 1,
      },
    ]);
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      width={currentStep === 1 ? '100%' : 900}
      footer={false}
      maskClosable={false}
      destroyOnClose
      title={
        kit ? (
          <StatusTag color="violet" label={kit.name.toUpperCase()} />
        ) : undefined
      }
    >
      <WorkorderItemsContext.Provider
        value={{
          items,
          setItems,
          itemType,
          setItemType,
          hasNewItem,
          setHasNewItem,
          isLastSetup,
          setIsLastSetup,
          currentStep,
          onNextStep,
          onPrevStep,
          kit,
        }}
      >
        <FragaPartsProvider>
          <WorkOrderSteps
            currentStep={currentStep}
            workorder={workorder}
            onCancel={handleCancel}
          />
        </FragaPartsProvider>
      </WorkorderItemsContext.Provider>
    </Modal>
  );
};

type ItemPrice = {
  price: number;
  companyId: number;
  cost?: number;
  realCost?: number;
  priceFixed?: number;
};

export function getCompanyPrice(
  prices: ItemPrice[],
  companyId: number,
  key: keyof ItemPrice = 'price',
) {
  const companyPrice = prices
    ?.map((i) => i)
    .find((price) => price.companyId === companyId);

  if (companyPrice) {
    return companyPrice[key] || 0;
  }

  return 0;
}

export default WorkOrderItemModal;
